import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Mail, Smartphone, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { LogoShield } from '@/components/LogoShield';
import { AltchaWidget } from '@/components/AltchaWidget';
import { invokeMfa } from '@/lib/mfa-invoke';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface MfaEnrollmentProps {
  userId: string;
  email: string;
  onComplete: (method: 'totp' | 'email') => void;
  initialMethod?: 'totp' | 'email' | null;
  showBackButton?: boolean;
  onBack?: () => void;
  existingMethods?: string[];
}

export function MfaEnrollment({ userId, email, onComplete, initialMethod = null, showBackButton = false, onBack, existingMethods = [] }: MfaEnrollmentProps) {
  const [selectedMethod, setSelectedMethod] = useState<'totp' | 'email' | null>(initialMethod);
  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupStep, setShowBackupStep] = useState(false);
  const [pendingMethod, setPendingMethod] = useState<'totp' | 'email' | null>(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [altchaToken, setAltchaToken] = useState<string | null>(null);
  
  // TOTP specific
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrCode, setTotpQrCode] = useState('');
  const [factorId, setFactorId] = useState('');
  const [usingExistingTotp, setUsingExistingTotp] = useState(false);
  
  // Email specific
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const getErrorMessage = (err: unknown) => {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    if (typeof err === 'object' && err && 'message' in err) {
      const msg = (err as { message?: unknown }).message;
      return typeof msg === 'string' ? msg : '';
    }
    return '';
  };

  const getErrorStatus = (err: unknown) => {
    if (typeof err !== 'object' || !err || !('status' in err)) return null;
    const status = (err as { status?: unknown }).status;
    return typeof status === 'number' ? status : null;
  };

  useEffect(() => {
    if (initialMethod === 'totp' && !totpQrCode) {
      enrollTotp();
    } else if (initialMethod === 'email' && !emailCodeSent) {
      enrollEmail();
    }
  }, [initialMethod]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const loadExistingTotpFactor = async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    const totpFactor = data?.totp?.find((factor) =>
      factor?.friendly_name === 'Authenticator App' || factor?.factor_type === 'totp',
    );
    if (!totpFactor) return false;

    setFactorId(totpFactor.id);
    setTotpSecret('');
    setTotpQrCode('');
    setSelectedMethod('totp');
    setUsingExistingTotp(true);
    return true;
  };

  const enrollTotp = async () => {
    setIsLoading(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.refreshSession();
      if (sessionError || !sessionData.session) {
        console.error('Session refresh error:', sessionError);
        toast.error('Session expired. Please log in again.');
        return;
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (error) throw error;

      setFactorId(data.id);
      setTotpSecret(data.totp.secret);
      setTotpQrCode(data.totp.qr_code);
      setSelectedMethod('totp');
      setUsingExistingTotp(false);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      const status = getErrorStatus(err);
      if (status === 422 || errorMessage.includes('already exists')) {
        try {
          const reused = await loadExistingTotpFactor();
          if (reused) {
            toast.message('Authenticator App already enrolled. Enter a code from your app to continue.');
            return;
          }
        } catch (lookupError) {
          console.error('Existing TOTP lookup failed:', lookupError);
        }
      }
      console.error('TOTP enrollment error:', errorMessage || err);
      toast.error('Failed to set up authenticator app');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyTotpEnrollment = async () => {
    if (code.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    if (!factorId) {
      toast.error('Missing authenticator factor. Please try enrolling again.');
      return;
    }

    setIsLoading(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });

      if (verifyError) throw verifyError;

      const newMethods = existingMethods.includes('totp') 
        ? existingMethods 
        : [...existingMethods, 'totp'];
      
      await supabase
        .from('profiles')
        .update({ mfa_method: newMethods.join(',') })
        .eq('user_id', userId);

      // notify-mfa-change is ALTCHA-exempt
      await invokeMfa({
        body: { action: 'notify-mfa-change', user_id: userId, change_type: 'enabled', method: 'totp' },
      });

      await supabase.auth.refreshSession();

      const hadNoMfa = existingMethods.length === 0;
      if (hadNoMfa) {
        await generateBackupCodesFlow('totp');
      } else {
        toast.success('Authenticator app enabled successfully!');
        onComplete('totp');
      }
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      console.error('TOTP verification error:', errorMessage || err);
      toast.error(errorMessage || 'Invalid verification code');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const enrollEmail = async () => {
    setIsLoading(true);
    try {
      const { error } = await invokeMfa({
        body: { action: 'send', email, user_id: userId },
        altchaToken,
      });

      if (error) throw error;

      setSelectedMethod('email');
      setEmailCodeSent(true);
      setCooldown(60);
      toast.success('Verification code sent to your email');
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      console.error('Email MFA enrollment error:', errorMessage || err);
      toast.error('Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmailEnrollment = async () => {
    if (code.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await invokeMfa({
        body: { action: 'verify', user_id: userId, code },
        altchaToken,
      });

      if (error || !data?.verified) {
        throw new Error((data as Record<string, unknown>)?.error as string || 'Invalid verification code');
      }

      const newMethods = existingMethods.includes('email') 
        ? existingMethods 
        : [...existingMethods, 'email'];
      
      await supabase
        .from('profiles')
        .update({ mfa_method: newMethods.join(',') })
        .eq('user_id', userId);

      // notify-mfa-change is ALTCHA-exempt
      await invokeMfa({
        body: { action: 'notify-mfa-change', user_id: userId, change_type: 'enabled', method: 'email' },
      });

      await supabase.auth.refreshSession();

      const hadNoMfa = existingMethods.length === 0;
      if (hadNoMfa) {
        await generateBackupCodesFlow('email');
      } else {
        toast.success('Email verification enabled successfully!');
        onComplete('email');
      }
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      console.error('Email verification error:', errorMessage || err);
      toast.error(errorMessage || 'Invalid verification code');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const resendEmailCode = async () => {
    if (cooldown > 0) return;
    
    setIsLoading(true);
    try {
      await invokeMfa({
        body: { action: 'send', email, user_id: userId },
        altchaToken,
      });

      setCooldown(60);
      toast.success('Verification code resent');
    } catch (err: unknown) {
      toast.error('Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const generateBackupCodesFlow = async (method: 'totp' | 'email') => {
    setBackupLoading(true);
    try {
      const { data, error } = await invokeMfa({
        body: { action: 'generate-backup-codes', user_id: userId },
        altchaToken,
      });
      if (error) throw error;
      setBackupCodes((data as Record<string, unknown>)?.codes as string[] || []);
      setPendingMethod(method);
      setShowBackupStep(true);
      toast.success('MFA enabled. Save your backup codes.');
    } catch (err) {
      console.error('Error generating backup codes:', err);
      toast.error('MFA enabled, but backup codes could not be generated. Please generate them from settings.');
      onComplete(method);
    } finally {
      setBackupLoading(false);
      setIsLoading(false);
    }
  };

  const handleConfirmBackupCodes = () => {
    if (!pendingMethod) return;
    setShowBackupStep(false);
    toast.success('Backup codes saved. You are now logged in.');
    onComplete(pendingMethod);
  };

  if (showBackupStep) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <Card className="w-full max-w-md cyber-card relative z-10">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <LogoShield className="h-8 w-8" />
              <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle className="text-xl flex items-center justify-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Save Your Backup Codes
            </CardTitle>
            <CardDescription>
              Store these one-time codes safely. They allow access if you lose your MFA device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, idx) => (
                <div key={idx} className="p-2 bg-muted/50 border border-border rounded font-mono text-sm text-center">
                  {code}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              These codes are shown only once. Keep them in a secure place.
            </p>
            <Button className="w-full" onClick={handleConfirmBackupCodes} disabled={backupLoading}>
              I've saved my backup codes
            </Button>
            {showBackButton && onBack && (
              <Button variant="ghost" className="w-full" onClick={onBack}>
                Cancel and sign in with a different account
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Method selection screen
  if (!selectedMethod) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <Card className="w-full max-w-md cyber-card relative z-10">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <LogoShield className="h-8 w-8" />
              <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle className="text-xl">Enable Two-Factor Authentication</CardTitle>
            <CardDescription>
              Two-factor authentication is required to protect your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <AltchaWidget onToken={setAltchaToken} className="flex justify-center" />

            <Button
              variant="outline"
              className="w-full h-auto py-4 flex items-start gap-4 justify-start"
              onClick={enrollTotp}
              disabled={isLoading}
            >
              <Smartphone className="h-8 w-8 text-primary shrink-0" />
              <div className="text-left">
                <div className="font-semibold">Authenticator App</div>
                <div className="text-sm text-muted-foreground">
                  Use Google Authenticator, Authy, or similar apps
                </div>
              </div>
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="block w-full"
                  tabIndex={!altchaToken ? 0 : -1}
                  aria-label={!altchaToken ? "Complete I'm Not A Robot before enabling Email Code" : undefined}
                >
                  <Button
                    variant="outline"
                    className="w-full h-auto py-4 flex items-start gap-4 justify-start"
                    onClick={enrollEmail}
                    disabled={isLoading || !altchaToken}
                  >
                    <Mail className="h-8 w-8 text-primary shrink-0" />
                    <div className="text-left">
                      <div className="font-semibold">Email Code</div>
                      <div className="text-sm text-muted-foreground">
                        Receive a code via email each time you log in
                      </div>
                    </div>
                  </Button>
                </span>
              </TooltipTrigger>
              {!altchaToken ? (
                <TooltipContent>
                  Complete I&apos;m Not A Robot first to enable Email Code.
                </TooltipContent>
              ) : null}
            </Tooltip>

            {showBackButton && onBack && (
              <Button variant="ghost" onClick={onBack} className="w-full mt-4">
                Cancel
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // TOTP enrollment screen
  if (selectedMethod === 'totp') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <Card className="w-full max-w-md cyber-card relative z-10">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <LogoShield className="h-8 w-8" />
              <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle className="text-xl flex items-center justify-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              Set Up Authenticator App
            </CardTitle>
            <CardDescription>
              {usingExistingTotp
                ? 'Use the authenticator app already linked to your account.'
                : 'Scan the QR code with your authenticator app'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {totpQrCode && (
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-lg">
                  <img src={totpQrCode} alt="QR Code" className="w-48 h-48" />
                </div>
              </div>
            )}

            {totpSecret && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Or enter this code manually:</p>
                <code className="text-xs bg-muted px-3 py-2 rounded font-mono break-all">
                  {totpSecret}
                </code>
              </div>
            )}

            {usingExistingTotp && !totpQrCode && !totpSecret && (
              <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground text-center">
                An authenticator app is already enrolled for this account. Enter the current 6-digit code to verify.
              </div>
            )}

            <div>
              <p className="text-sm text-muted-foreground text-center mb-4">
                Enter the 6-digit code from your app:
              </p>
              <div className="flex justify-center">
                <InputOTP
                  value={code}
                  onChange={setCode}
                  maxLength={6}
                  disabled={isLoading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="bg-muted/50 border-border" />
                    <InputOTPSlot index={1} className="bg-muted/50 border-border" />
                    <InputOTPSlot index={2} className="bg-muted/50 border-border" />
                    <InputOTPSlot index={3} className="bg-muted/50 border-border" />
                    <InputOTPSlot index={4} className="bg-muted/50 border-border" />
                    <InputOTPSlot index={5} className="bg-muted/50 border-border" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

          <Button 
            onClick={verifyTotpEnrollment} 
            className="w-full" 
            disabled={isLoading || code.length !== 6}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Enable Authenticator'
            )}
          </Button>

          <Button 
            variant="ghost" 
            onClick={() => {
              setSelectedMethod(null);
              setCode('');
            }} 
            className="w-full"
          >
            Choose Different Method
          </Button>
          {showBackButton && onBack && (
            <Button variant="ghost" className="w-full" onClick={onBack}>
              Cancel and sign in with a different account
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

  // Email enrollment screen
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <Card className="w-full max-w-md cyber-card relative z-10">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <LogoShield className="h-8 w-8" />
            <span className="text-2xl font-mono font-bold text-foreground">ShadowScan</span>
          </div>
          <CardTitle className="text-xl flex items-center justify-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Set Up Email Verification
          </CardTitle>
          <CardDescription>
            Enter the 6-digit code sent to {email}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <InputOTP
              value={code}
              onChange={setCode}
              maxLength={6}
              disabled={isLoading}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="bg-muted/50 border-border" />
                <InputOTPSlot index={1} className="bg-muted/50 border-border" />
                <InputOTPSlot index={2} className="bg-muted/50 border-border" />
                <InputOTPSlot index={3} className="bg-muted/50 border-border" />
                <InputOTPSlot index={4} className="bg-muted/50 border-border" />
                <InputOTPSlot index={5} className="bg-muted/50 border-border" />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {!altchaToken && (
            <AltchaWidget onToken={setAltchaToken} className="flex justify-center" />
          )}

          <Button 
            onClick={verifyEmailEnrollment} 
            className="w-full" 
            disabled={isLoading || code.length !== 6 || !altchaToken}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Enable Email Verification'
            )}
          </Button>

          <Button
            variant="ghost"
            onClick={resendEmailCode}
            disabled={isLoading || cooldown > 0 || !altchaToken}
            className="w-full"
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend Code'}
          </Button>

          <Button 
            variant="ghost" 
            onClick={() => {
              setSelectedMethod(null);
              setCode('');
            }} 
            className="w-full"
          >
            Choose Different Method
          </Button>
          {showBackButton && onBack && (
            <Button variant="ghost" className="w-full" onClick={onBack}>
              Cancel and sign in with a different account
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
